import "./flex-table.less"

const verticalContainerClass = "flex-table-vertical-container"
const horizontalContainerClass = "flex-table-horizontal-container"

export class FlexTableContext {
    /**
     * Create a new flex table context using the given table element as the basis.
     * @param {HTMLTableElement} table A table element to use as the context.
     */
    constructor(table, options) {
        this.table = table
        // Create a pipeline for compiling cloned HTML elements based on the original elements.
        this.compilers = []
        // Create a pipeline for extending functionality, such as adding a horizontal scroll carousel.
        this.extensions = []
        // Additional options, such as number of frozen headers/columns,
        this.options = options
        // Additional options that will be defined dynamically as extensions are added.
        this.extensionOptions = {};
    }

    attach() {
        // Initialize the flex table based on the context.
        if (!this.verticalContainer || !this.verticalContainer) {
            this.verticalContainer = createVerticalContainer()
            this.horizontalContainer = createHorizontalContainer()
            /** @type {HTMLElement} */
            this.scrollContainer = this.options.scrollContainer || window
            // Wrap the table in two parent divs in order move it in 2D space.
            this.table.parentElement.insertBefore(this.verticalContainer, this.table) // Vertical container before table
            this.verticalContainer.appendChild(this.horizontalContainer) // Horizontal container within vertical container
            this.horizontalContainer.appendChild(this.table) // Table now within both containers.
        }

        for (const extension of this.extensions) {
            // Apply each extension to the context.

            try {
                extension.attach(this)
            } catch (ex) {
                console.error(`Unable to attach extension ${extension.__proto__.constructor.name}`)
                console.error(ex)
            }
        }
    }

    detach() {
        // Clean up resources used to initialize the flex table.
        this.extensions.reduceRight((_, extension) => {
            try {
                extension.detach(this)
            } catch (ex) {
                console.error(`Unable to detach extension ${extension.__proto__.constructor.name}`)
                console.error(ex)
            }
            return null
        }, null)
    }

    getCellHandlers(extension, eventName, cell) {
        const options = this.extensionOptions[extension] || (this.extensionOptions[extension] = {});
        const handlers = options.handlers || (options.handlers = {});
        const specificHandlers = handlers[eventName] || (handlers[eventName] = []);
        const cellHandlers = specificHandlers.find(p => p.cell === cell);
        return cellHandlers ? cellHandlers.handlers : [];
    }

    /**
     * Return the cell mapper for the given cell and extension, if any.
     * @param {string} extension The extension name used for mappign
     * @param {HTMLElement} cell The table cell element being searched for.
     * @returns An object exposing the getter and setter for the cell.
     */
    getCellMapper(extension, cell) {
        const options = this.extensionOptions[extension] || (this.extensionOptions[extension] = {});
        const mappers = options.mappers || (options.mappers = []);
        // Add mapper and remove any existing mapper for the same cell.
        const cellMapper = mappers.find(p => p.cell === cell);
        return cellMapper ? cellMapper.mapper : null;
    }

    /**
     * Register the given cell with a handler for specific events.
     * @param {string} extension The name of the extension using the mapping.
     * @param {string} eventName The name of the event being handled.
     * @param {HTMLElement} cell The cell element that will be passed to the mappers.
     * @param {{ getValue: () => any, setValue: (value: any) => void }} mapper A mapper object
     */
    addCellHandler(extension, eventName, cell, handler) {
        const options = this.extensionOptions[extension] || (this.extensionOptions[extension] = {});
        const handlers = options.handlers || (options.handlers = {});
        const specificHandlers = handlers[eventName] || (handlers[eventName] = []);
        let cellHandlers = specificHandlers.find(p => p.cell === cell);
        if (!cellHandlers) {
            cellHandlers = { cell, handlers: [] };
            specificHandlers.push(cellHandlers);
        }
        cellHandlers.handlers.push(handler);
    }

    /**
     * Unregister the given cell with a handler for specific events.
     * @param {string} extension The name of the extension using the mapping.
     * @param {string} eventName The name of the event being handled.
     * @param {HTMLElement} cell The cell element that will be passed to the mappers.
     * @param {{ getValue: () => any, setValue: (value: any) => void }} mapper A mapper object
     */
    removeCellHandler(extension, eventName, cell, handler) {
        const options = this.extensionOptions[extension];
        const handlers = options ? options.handlers : null;
        const specificHandlers = handlers ? handlers[eventName] : null;
        const cellHandlers = specificHandlers ? specificHandlers.find(p => p.cell === cell) : null;
        if (cellHandlers) {
            cellHandlers.handlers = cellHandlers.handlers.filter(p => p !== handler);
        }
    }

    /**
     * Register the given cell as having a mapped value for a particular extension.
     * @param {string} extension The name of the extension using the mapping.
     * @param {HTMLElement} cell The cell element that will be passed to the mappers.
     * @param {{ getValue: () => any, setValue: (value: any) => void }} mapper A mapper object
     */
    registerCellMapper(extension, cell, mapper) {
        const options = this.extensionOptions[extension] || (this.extensionOptions[extension] = {});
        const mappers = options.mappers || (options.mappers = []);
        // Add mapper and remove any existing mapper for the same cell.
        options.mappers = mappers.filter(p => p.cell !== cell)
            .concat({ cell, mapper });
    }
}

export class FlexTableExtension {
    constructor() {
    }

    attach(flexTableContext) {
        this.table = flexTableContext.table
        this.options = flexTableContext.options
        this.compilers = flexTableContext.compilers
        this.scrollContainer = flexTableContext.scrollContainer
        this.verticalContainer = flexTableContext.verticalContainer
        this.horizontalContainer = flexTableContext.horizontalContainer

        this.isAttached = true
    }

    detach(flexTableContext) {
        this.isAttached = false

        this.table = null
        this.options = null
        this.compilers = null
        this.scrollContainer = null
        this.verticalContainer = null
        this.horizontalContainer = null
    }
}

/**
 * Create and return a new vertical container element.
 * @returns {HTMLDivElement}
 */
function createVerticalContainer() {
    const verticalContainer = document.createElement("div");
    verticalContainer.classList.add(verticalContainerClass);
    return verticalContainer;
}

/**
 * Create and return a new horizontal container element.
 * @returns {HTMLDivElement}
 */
function createHorizontalContainer() {
    const horizontalContainer = document.createElement("div")
    horizontalContainer.classList.add(horizontalContainerClass)
    return horizontalContainer
}
